import { Extension } from '@tiptap/core'
import hljs from 'highlight.js/lib/core'
import bash from 'highlight.js/lib/languages/bash'
import c from 'highlight.js/lib/languages/c'
import cpp from 'highlight.js/lib/languages/cpp'
import csharp from 'highlight.js/lib/languages/csharp'
import css from 'highlight.js/lib/languages/css'
import dockerfile from 'highlight.js/lib/languages/dockerfile'
import go from 'highlight.js/lib/languages/go'
import java from 'highlight.js/lib/languages/java'
import javascript from 'highlight.js/lib/languages/javascript'
import json from 'highlight.js/lib/languages/json'
import markdown from 'highlight.js/lib/languages/markdown'
import php from 'highlight.js/lib/languages/php'
import python from 'highlight.js/lib/languages/python'
import ruby from 'highlight.js/lib/languages/ruby'
import rust from 'highlight.js/lib/languages/rust'
import sql from 'highlight.js/lib/languages/sql'
import typescript from 'highlight.js/lib/languages/typescript'
import xml from 'highlight.js/lib/languages/xml'
import yaml from 'highlight.js/lib/languages/yaml'
import { highlightPlugin } from 'prosemirror-highlightjs'

/**
 * We deliberately register only a curated set of languages with highlight.js's
 * tree-shakeable core build (`highlight.js/lib/core`) instead of pulling in
 * `lowlight` (which bundles highlight.js's ~190 languages by default). This keeps
 * the bundle small while covering the languages wiki authors are most likely to use.
 */
const LANGUAGES: [id: string, def: typeof typescript][] = [
  ['bash', bash],
  ['c', c],
  ['cpp', cpp],
  ['csharp', csharp],
  ['css', css],
  ['dockerfile', dockerfile],
  ['go', go],
  ['java', java],
  ['json', json],
  ['markdown', markdown],
  ['php', php],
  ['python', python],
  ['ruby', ruby],
  ['rust', rust],
  ['sql', sql],
  ['typescript', typescript],
  ['javascript', javascript],
  ['xml', xml],
  ['yaml', yaml],
]

for (const [id, def] of LANGUAGES) {
  hljs.registerLanguage(id, def)
}

/** Options for the code block's language selector toolbar dropdown. */
export const CODE_BLOCK_LANGUAGES: { label: string, value: string }[] = [
  { label: 'Auto-detect', value: '' },
  { label: 'Bash', value: 'bash' },
  { label: 'C', value: 'c' },
  { label: 'C++', value: 'cpp' },
  { label: 'C#', value: 'csharp' },
  { label: 'CSS', value: 'css' },
  { label: 'Dockerfile', value: 'dockerfile' },
  { label: 'Go', value: 'go' },
  { label: 'HTML/XML', value: 'xml' },
  { label: 'Java', value: 'java' },
  { label: 'JavaScript', value: 'javascript' },
  { label: 'JSON', value: 'json' },
  { label: 'Markdown', value: 'markdown' },
  { label: 'PHP', value: 'php' },
  { label: 'Python', value: 'python' },
  { label: 'Ruby', value: 'ruby' },
  { label: 'Rust', value: 'rust' },
  { label: 'SQL', value: 'sql' },
  { label: 'TypeScript', value: 'typescript' },
  { label: 'YAML', value: 'yaml' },
]

/**
 * Syntax-highlights `codeBlock` nodes using the `prosemirror-highlightjs` ProseMirror
 * plugin (decoration-based) rather than `@tiptap/extension-code-block-lowlight`, which
 * pulls in the full `lowlight`/`highlight.js` "common" language bundle.
 *
 * The language is read from the node's own `language` attribute (set via the toolbar's
 * language selector, or parsed from a fenced code block's info string). When no language
 * is set, highlight.js's auto-detection is used for display only - the detected language
 * is intentionally not persisted back onto the node (see the no-op setter below), since
 * `language` is a schema attribute that should only ever reflect an explicit user choice.
 */
export const CodeBlockHighlight = Extension.create({
  name: 'codeBlockHighlight',

  addProseMirrorPlugins() {
    return [
      highlightPlugin(
        hljs,
        ['codeBlock'],
        node => (node.attrs.language as string | null) || '',
        () => null,
      ),
    ]
  },
})
